/**
 * Detect a cookie-consent blocker that pre-hides banners by injecting a
 * <style> at document-start, so the checkout can work around the damage it does
 * to MobiusPay's hosted card fields.
 *
 * Blockers such as Ghostery's bundled DuckDuckGo "autoconsent" (and other
 * never-consent tools) insert a <style id="autoconsent-prehide"> holding rules
 * for known consent-popup selectors (#didomi-popup, OneTrust, Usercentrics, …)
 * into every document at document-start, then remove it a second or two later
 * when they find no banner. That style lands inside MobiusPay's card iframes
 * too; MobiusPay keys its field styling off the first <style> in the iframe, so
 * when the blocker removes its element our theme and the field's opacity:1
 * reveal rule go with it and the card field renders as a blank box. See Asana
 * 1218303811765924.
 *
 * We cannot see into the cross-origin iframe, but the same blocker pre-hides our
 * own top document, so we watch for its signature here at startup and remember
 * it. mountCollectJs reads the flag and defers the hosted-field styling only
 * when a blocker is present; every other browser keeps the unchanged flow.
 */
let seen = false;
let started = false;

const SIGNATURE_ID = 'autoconsent-prehide';
const SIGNATURE_TEXT =
  /didomi|autoconsent|onetrust|usercentrics|truste|cookiebot|qc-cmp|osano|cookie-?consent|sp_message_container/i;

function isBlockerStyle(node: Node): boolean {
  if (!(node instanceof HTMLStyleElement)) return false;
  if (node.id === SIGNATURE_ID) return true;
  const text = node.textContent ?? '';
  return text.length > 0 && SIGNATURE_TEXT.test(text);
}

/**
 * Start watching the document for a consent-blocker pre-hide style. Safe to call
 * more than once; only the first call does anything. Call as early as possible
 * (app entry) because the pre-hide style is removed within ~2s.
 */
export function initConsentBlockerProbe(): void {
  if (started || typeof document === 'undefined') return;
  started = true;

  const scanExisting = (): boolean => {
    for (const style of Array.from(document.querySelectorAll('style'))) {
      if (isBlockerStyle(style)) {
        seen = true;
        return true;
      }
    }
    return false;
  };
  if (scanExisting()) return;

  const observer = new MutationObserver((records) => {
    for (const record of records) {
      for (const node of record.addedNodes) {
        if (isBlockerStyle(node)) {
          seen = true;
          observer.disconnect();
          return;
        }
      }
    }
  });
  try {
    observer.observe(document.documentElement, { childList: true, subtree: true });
  } catch {
    // documentElement missing at import time is not expected; nothing to watch.
  }
  // The pre-hide style is gone within ~2s; stop watching a little after.
  window.setTimeout(() => observer.disconnect(), 4000);
}

/** Whether a consent-blocker pre-hide style was seen on this page. */
export function consentBlockerSeen(): boolean {
  return seen;
}
